<template>
  <NotificationSettingRow>
    <template #title>Send now</template>
    <template #description>
      <p v-if="rowState === 'loading'" role="status">
        Checking notification status…
      </p>
      <div
        v-else-if="rowState === 'unavailable'"
        class="alert alert-subtle-warning py-2"
        role="status"
      >
        Notification status is unavailable.
        <span v-if="isPolling">Retrying automatically…</span>
        <button
          v-else
          type="button"
          class="btn btn-sm btn-outline-primary ms-2"
          @click="retryStatus"
        >
          Retry status
        </button>
      </div>
      <p v-else-if="rowState === 'releasing'">Sending notifications…</p>
      <p v-else-if="rowState === 'pending'">
        Email the transaction notification to every company with pending
        transactions.
      </p>
      <p v-else data-testid="no-pending-notifications">
        No pending transaction notifications.
      </p>
      <p v-if="status?.rollup_queued_count">
        <font-awesome-icon icon="clock" />
        {{ status.rollup_queued_count }} companies held for monthly rollup
        (sends on the 25th)
      </p>
    </template>
    <template #control>
      <ActionButtonWithSpinner
        v-if="status && (status.count > 0 || isReleasing)"
        icon="envelope"
        data-testid="release-notifications"
        :busy="isReleasing"
        :disabled="observationState !== 'ready'"
        @click="releaseNotifications"
      >
        Send to {{ status.count }} companies
      </ActionButtonWithSpinner>
    </template>
  </NotificationSettingRow>
</template>

<script setup lang="ts">
import { useIntervalFn } from "@vueuse/core";
import { computed, onScopeDispose, ref, watch } from "vue";
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import {
  AsyncTaskStartResponse,
  useAsyncTask,
} from "@/composables/useAsyncTask";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import NotificationSettingRow from "./components/NotificationSettingRow.vue";

interface NotificationStatus {
  count: number;
  rollup_queued_count?: number;
  release_in_progress: boolean;
}

interface ReleaseResult {
  outcome: "released" | "already_running";
}

// An idle admin tab only needs to notice a release started elsewhere or new pending invoices;
// each status check loads every pending invoice row, so idle tabs check sparingly. While a
// release runs, or the last check failed, the tab checks often to free the button (or recover)
// within seconds.
const IDLE_POLL_INTERVAL_MS = 30000;
const ACTIVE_POLL_INTERVAL_MS = 5000;
const STATUS_REQUEST_TIMEOUT_MS = 15000;
const MAX_CONSECUTIVE_FAILURES = 3;

const { globalErrorHandler, globalSuccessHandler, globalWarningHandler } =
  useGlobalHandlers();
const status = ref<NotificationStatus | null>(null);
const observationState = ref<"loading" | "ready" | "unavailable">("loading");
const releaseTask = useAsyncTask<ReleaseResult>();
const requests = new AbortController();
let refreshing = false;
let consecutiveFailures = 0;

const isReleasing = computed(
  () =>
    releaseTask.isRunning.value || status.value?.release_in_progress === true
);
// One state drives the row's description so it can never contradict the button (e.g. "No
// pending" next to a spinner while a release drains the count to zero).
const rowState = computed(() => {
  if (observationState.value !== "ready") return observationState.value;
  if (isReleasing.value) return "releasing";
  return (status.value?.count ?? 0) > 0 ? "pending" : "empty";
});
const pollIntervalMs = computed(() =>
  isReleasing.value || observationState.value === "unavailable"
    ? ACTIVE_POLL_INTERVAL_MS
    : IDLE_POLL_INTERVAL_MS
);

// Observe idle tabs too: another admin can start a release after this page loads. The
// interval is reactive, so useIntervalFn restarts the timer whenever the cadence changes.
const {
  isActive: isPolling,
  pause,
  resume,
} = useIntervalFn(refreshStatus, pollIntervalMs, { immediate: false });

onScopeDispose(() => {
  requests.abort();
  releaseTask.reset();
});

watch(releaseTask.state, (state) => {
  if (state === "success") {
    if (releaseTask.result.value?.outcome === "already_running") {
      globalWarningHandler(
        "Release already running",
        "Another release was in progress, so this one did nothing."
      );
    } else {
      globalSuccessHandler(
        "Notification emails sent",
        "The notification emails have been scheduled successfully"
      );
    }
  } else if (state === "failed") {
    globalErrorHandler(
      releaseTask.error.value ??
        new Error(releaseTask.errorMessage.value ?? "Release failed")
    );
  } else {
    return;
  }

  releaseTask.reset();
  void refreshStatus();
});

async function refreshStatus() {
  // A completion refresh and a timer tick must not overlap and apply snapshots out of order.
  if (
    refreshing ||
    consecutiveFailures >= MAX_CONSECUTIVE_FAILURES ||
    requests.signal.aborted
  )
    return;
  refreshing = true;
  try {
    const response = await goSolidApi.get<NotificationStatus | null>(
      "/api/admin/pending-notification-email-count",
      { signal: requests.signal, timeout: STATUS_REQUEST_TIMEOUT_MS }
    );
    if (requests.signal.aborted) return;
    if (!response.data || !Number.isFinite(response.data.count)) {
      throw new Error("Notification status is unavailable.");
    }
    status.value = response.data;
    observationState.value = "ready";
    consecutiveFailures = 0;
  } catch (error: unknown) {
    if (requests.signal.aborted) return;
    observationState.value = "unavailable";
    consecutiveFailures += 1;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      pause();
      globalErrorHandler(error);
    }
  } finally {
    refreshing = false;
  }
}

function retryStatus() {
  observationState.value = "loading";
  consecutiveFailures = 0;
  resume();
  void refreshStatus();
}

function releaseNotifications() {
  if (isReleasing.value || observationState.value !== "ready") return;
  void releaseTask.execute(async () => {
    const response = await goSolidApi.post<AsyncTaskStartResponse>(
      "/api/admin/send-notification-emails"
    );
    return response.data;
  });
}

retryStatus();
</script>
